// Timezone-safe date helpers for the Front Desk. All operate on "YYYY-MM-DD" strings and never touch
// toISOString() (which converts to UTC and shifts the day in WIB). "Today" is the Asia/Jakarta (WIB)
// calendar day regardless of the browser's own timezone.
const DAYS = {
  id: ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"],
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
};

const MONTHS = {
  id: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "Mei",
    "Jun",
    "Jul",
    "Agu",
    "Sep",
    "Okt",
    "Nov",
    "Des",
  ],
  en: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
};

// Intl.DateTimeFormat construction is one of the heavier browser calls (locale + timezone
// resolution), so build the WIB formatters once and reuse them — the formatters are stateless.
const WIB_DATE_FMT = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Jakarta",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const WIB_TIME_FMT = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Asia/Jakarta",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

// Shift a YYYY-MM-DD string by N days, staying on the local calendar (no UTC conversion).
export const shiftIso = (isoDate, days) => {
  const [year, month, day] = isoDate.split("-").map(Number);
  const dt = new Date(year, month - 1, day);
  dt.setDate(dt.getDate() + days);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Today's date on the WIB (Asia/Jakarta) calendar, as YYYY-MM-DD.
export const wibTodayIso = () => WIB_DATE_FMT.format(new Date());

// "Kam, 02 Jul 2026" (id) / "Thu, 02 Jul 2026" (en) from a YYYY-MM-DD string.
// pad=false gives the non-padded day used in body copy ("Kam, 2 Jul 2026").
export const formatDate = (isoDate, lang, pad = true) => {
  if (!isoDate) {
    return "";
  }
  const [year, month, day] = isoDate.split("-").map(Number);
  const weekday = new Date(year, month - 1, day).getDay();
  const days = DAYS[lang] || DAYS.id;
  const months = MONTHS[lang] || MONTHS.id;
  const dayStr = pad ? String(day).padStart(2, "0") : String(day);
  return `${days[weekday]}, ${dayStr} ${months[month - 1]} ${year}`;
};

// "Kam, 2 Jul 2026, 12:22:05 WIB" from an ISO instant (24h, WIB, non-padded day).
export const formatDateTime = (isoInstant, lang) => {
  if (!isoInstant) {
    return "";
  }
  const instant = new Date(isoInstant);
  return `${formatDate(wibDateIso(instant), lang, false)}, ${wibTime(instant)} WIB`;
};

// WIB wall-clock time "HH:MM:SS" for a given Date instant.
export const wibTime = (instant) => WIB_TIME_FMT.format(instant);

// WIB date as YYYY-MM-DD for a given Date instant (for the ticking clock's date part).
export const wibDateIso = (instant) => WIB_DATE_FMT.format(instant);
